export function formatEthSpot(value: number): string {
  if (!Number.isFinite(value) || value <= 0)
    return '0 ETH'
  if (value >= 1)
    return `${value.toFixed(4)} ETH`
  if (value >= 0.0001)
    return `${value.toFixed(6)} ETH`
  const decimals = value >= 1e-8 ? 12 : 18
  const trimmed = value.toFixed(decimals).replace(/0+$/, '').replace(/\.$/, '')
  return `${trimmed} ETH`
}

export function formatQuoteAmount(value: number, quote?: 'usd' | 'eth'): string {
  return quote === 'eth' ? formatEthSpot(value) : formatCurrency(value)
}

export function formatCurrency(value: number): string {
  if (value >= 1_000_000)
    return `$${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000)
    return `$${(value / 1_000).toFixed(1)}K`
  if (value >= 1)
    return `$${value.toFixed(2)}`
  if (value <= 0)
    return '$0.00'
  // Sub-$1 bonding curve prices (e.g. $0.042, $0.0000075) need more precision
  // than a flat 2 decimals, or every early-curve price would render as $0.
  const decimals = value < 0.0001 ? 8 : 6
  const trimmed = value.toFixed(decimals).replace(/0+$/, '').replace(/\.$/, '')
  return `$${trimmed}`
}

export function formatNumber(value: number): string {
  return value.toLocaleString('en-US')
}

function remainingMs(isoDate: string, nowMs = Date.now()): number {
  return new Date(isoDate).getTime() - nowMs
}

function padTime(value: number): string {
  return String(Math.max(0, value)).padStart(2, '0')
}

export function formatCountdownClock(isoDate: string, nowMs = Date.now()): string {
  const diff = remainingMs(isoDate, nowMs)
  if (diff <= 0)
    return 'Ended'
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)
  if (days > 0)
    return `${days}d ${padTime(hours)}h ${padTime(minutes)}m ${padTime(seconds)}s`
  if (hours > 0)
    return `${hours}h ${padTime(minutes)}m ${padTime(seconds)}s`
  return `${minutes}m ${padTime(seconds)}s`
}

export function getTimeLeft(isoDate: string, nowMs = Date.now()): string {
  const clock = formatCountdownClock(isoDate, nowMs)
  if (clock === 'Ended')
    return 'Ended'
  return `${clock} left`
}

export function getTimeUntilLive(isoDate: string, nowMs = Date.now()): string {
  const diff = remainingMs(isoDate, nowMs)
  if (diff <= 0)
    return 'Live in moments'
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  if (days > 0)
    return `Live in ${days}d ${hours}h`
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  if (hours > 0)
    return `Live in ${hours}h ${minutes}m`
  return `Live in ${minutes}m`
}

/**
 * Returns the label to show next to a project's participants/time row.
 * - Live presales show a ticking "Presale Ends:" countdown until the end date.
 * - Upcoming projects with a known start time show a countdown until they go live.
 * - Live launchpad projects keep the Live badge without an end timer.
 * - Everything else falls back to the existing "time left until end" label.
 */
export function getProjectCountdownLabel(project: { type?: string; status: string; startsAt?: string; endsAt: string }, nowMs = Date.now()): string | null {
  if (project.status === 'upcoming' && project.startsAt)
    return getTimeUntilLive(project.startsAt, nowMs)
  if (project.type === 'presale' && project.status === 'live' && project.endsAt) {
    const clock = formatCountdownClock(project.endsAt, nowMs)
    if (clock === 'Ended')
      return null
    return `Presale Ends: ${clock}`
  }
  if (project.status === 'live')
    return null
  return getTimeLeft(project.endsAt, nowMs)
}

export function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime()
  if (!Number.isFinite(diff) || diff < 0)
    return 'just now'
  const minutes = Math.floor(diff / (1000 * 60))
  if (minutes < 1)
    return 'just now'
  if (minutes < 60)
    return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24)
    return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}
